import { memo, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { login } from '../../../services/admin';
import { setAccessToken, setUserType } from '../../../utils/common';

import { Checkbox } from '../../../components/common';
import { Loader } from '../../../components/loader';
import { MainContext } from '../../../context';
import routes from '../../../routes';
import './Login.scss';
import { styled, useTheme } from '@mui/system';
import { Alert, useMediaQuery } from '@mui/material';
import { getLocalStorage, setLocalStorage } from '../../../utils/storage';

export const LoginFormContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  maxWidth: 1000,
  margin: '0 auto',
  padding: '160px 0px',
  flexDirection: 'column',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    maxWidth: 600,
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    padding: '64px 24px',
  },
}));

export const LoginFormWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  width: 872,
  height: 600,
  boxShadow: '0px 0px 48px 0px rgba(0, 0, 0, 0.25)',
  borderRadius: '16px',
  justifyContent: 'center',
  alignItems: 'flex-start',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    width: 600,
    height: 'auto',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const LoginFormForm = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'start',
  alignItems: 'center',
  background: '#edf2f4',
  overflow: 'hidden',
  flex: '1 0 0',
  alignSelf: 'stretch',
  [theme.breakpoints.down('md')]: {
    overflow: 'visible',
    width: 600,
    flex: '1',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const LoginFormContents = styled('form')({
  marginTop: 41,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '24px',
  padding: '0 61px',
  flexShrink: '1',
  // box-sizing: border-box,
  width: '100%',
});

export const LoginFormFields = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: 8,
});

export const LoginFormFieldWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  width: '400px',
  height: '45px',
  padding: '0px 16px',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '16px',
  borderRadius: '8px',
  border: '1px solid #a8a8a8',
  background: '#fff',

  '& input': {
    color: '#a8a8a8',

    /* ywh-caption */
    fontFamily: 'Montserrat',
    fontSize: '17px',
    fontWeight: '400',
    lineHeight: '27.54px' /* 162% */,
    width: '100%',
    border: 'none',
    background: 'white',
    '&:focus': {
      outline: 'none',
    },
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const LoginFormIcon = styled('div')({
  display: 'flex',
  paddingRight: '16px',
  alignItems: 'center',
  alignSelf: 'stretch',
  borderRight: '1px solid #a8a8a8',
});

export const LoginFormImage = styled('img')(({ theme }) => ({
  width: 350,
  flexShrink: 0,
  alignSelf: 'stretch',
  background: 'url(<path-to-image>), lightgray 50% / cover no-repeat',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    height: 300,
  },
}));

export const LoginFormFooter = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 40,
  alignSelf: 'stretch',
  paddingBottom: 32,
}));

export const LoginFormFooterCol = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 16,
  alignSelf: 'stretch',
  '& button': {
    padding: '16px 32px',
    borderRadius: 8,
    background: '#04246b',
    color: '#fff',
    textAlign: 'center',
    fontFamily: 'Montserrat',
    fontSize: 19,
    fontWeight: 500,
    lineHeight: '19px',
  },
  '& a': {
    color: '#04246b !important',
    textAlign: 'center',
    fontFamily: 'Montserrat',
    fontSize: 17,
    fontWeight: 600,
    lineHeight: '27.54px',
  },
});

export const LoginFormHeading = styled('div')({
  display: 'flex',
  padding: '32px 0',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 8,
  alignSelf: 'stretch',
  '& h3': {
    color: '#000',
    textAlign: 'center',
    fontFamily: 'Montserrat',
    fontSize: 25,
    fontWeight: 400,
    lineHeight: '25px',
  },
  '& h2': {
    color: '#000',
    textAlign: 'center',
    fontFamily: 'Playfair Display',
    fontSize: 30,
    fontWeight: 700,
    lineHeight: '39px',
    '& span': {
      color: '#cda07d',
      fontFamily: 'Playfair Display',
      fontSize: 30,
      fontWeight: 700,
      lineHeight: '39px',
    },
  },
  borderBottom: '1px solid #d8d8d8',
  boxShadow: '0px 2px 4px 0px #d8d8d8',
});

const LoginFormFooterColRegister = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  alignSelf: 'stretch',
  flexWrap: 'wrap',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: 24,
    alignItems: 'center',
  },
}));

const LoginFormFooterColRegisterText = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  '& span': {
    color: '#000',
    fontFamily: 'Montserrat',
    fontSize: 17,
    fontWeight: 400,
    lineHeight: '27.54px', // 162%
  },
});

const LoginFormFooterColRegisterTextCol = styled('div')({
  display: 'flex',
  paddingRight: 0,
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: 5,
  '& p': {
    color: '#000',
    fontFamily: 'Montserrat',
    fontSize: 17,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '17px',
  },
  '& a': {
    color: '#04246b !important',
    fontFamily: 'Montserrat',
    fontSize: 17,
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '27.54px', // 162%
  },
});

export const LoginFormFooterTerms = styled('p')({
  color: '#000',
  textAlign: 'center',
  fontFamily: 'Montserrat',
  fontSize: '13px !important',
  fontWeight: '400 !important',
  lineHeight: '21.06px !important',
  width: '100%',
  '& a': {
    color: '#04246b !important',
    fontFamily: 'Montserrat',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '21.06px',
  },
});

export const InputPassword = ({ name, required = true, onChange, placeholder }) => {
  const [togglePassword, setTogglePassword] = useState(false);
  return (
    <LoginFormFieldWrapper>
      <LoginFormIcon>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="26" viewBox="0 0 24 26" fill="none">
          <path
            d="M10.5 0.25C8.9087 0.25 7.38258 0.882141 6.25736 2.00736C5.13214 3.13258 4.5 4.6587 4.5 6.25C4.5 7.8413 5.13214 9.36742 6.25736 10.4926C7.38258 11.6179 8.9087 12.25 10.5 12.25C12.0913 12.25 13.6174 11.6179 14.7426 10.4926C15.8679 9.36742 16.5 7.8413 16.5 6.25C16.5 4.6587 15.8679 3.13258 14.7426 2.00736C13.6174 0.882141 12.0913 0.25 10.5 0.25ZM3.0135 13.75C2.61845 13.7484 2.22698 13.8248 1.86154 13.9749C1.49609 14.1249 1.16386 14.3456 0.883889 14.6243C0.603921 14.903 0.381727 15.2343 0.230049 15.5991C0.0783718 15.9638 0.000194268 16.355 0 16.75C0 19.2865 1.2495 21.199 3.2025 22.4455C4.7745 23.4475 6.795 24.0175 9 24.19V22.75C9 22.135 9.243 21.58 9.6615 21.1615L13.5315 17.2915C13.5108 17.0801 13.5002 16.8679 13.5 16.6555C13.4934 15.6324 13.7684 14.6272 14.295 13.75H3.015H3.0135ZM10.7235 25.528C10.7915 25.599 10.8732 25.6553 10.9638 25.6935C11.0543 25.7316 11.1517 25.7509 11.25 25.75H14.25C14.3931 25.7499 14.534 25.7138 14.6595 25.645C14.7594 25.5848 14.8376 25.4945 14.883 25.387C14.9305 25.2745 14.9618 25.1558 14.976 25.0345C14.9917 24.9066 14.9997 24.7779 15 24.649V24.25C15.36 24.2425 15.6405 24.2305 15.8445 24.2155C16.0128 24.2131 16.1746 24.1503 16.3005 24.0385C16.4106 23.9159 16.473 23.7578 16.476 23.593C16.491 23.398 16.5 23.1175 16.5 22.75H17.25C17.415 22.75 17.547 22.7155 17.649 22.645C17.7465 22.5789 17.8267 22.4904 17.883 22.387C17.94 22.2772 17.9757 22.1576 17.988 22.0345C18.003 21.9025 18.012 21.7645 18.012 21.625C18.0042 21.5117 18.0002 21.3981 18 21.2845V20.992C18.258 21.0865 18.519 21.1525 18.7845 21.1915C19.0515 21.2305 19.32 21.25 19.5945 21.25C20.1783 21.2489 20.7557 21.1289 21.2916 20.8973C21.8275 20.6657 22.3106 20.3274 22.7115 19.903C23.1165 19.48 23.433 19 23.6595 18.4615C23.886 17.9215 24 17.3515 24 16.75C24 16.1245 23.883 15.5395 23.649 14.992C23.4233 14.4582 23.0968 13.9728 22.6875 13.5625C22.277 13.1537 21.7917 12.8278 21.258 12.6025C20.7023 12.3659 20.1039 12.246 19.5 12.25C18.9094 12.261 18.3258 12.38 17.778 12.601C17.2438 12.8161 16.7575 13.1347 16.347 13.5385C15.9351 13.9429 15.6015 14.4199 15.363 14.9455C15.1197 15.4825 14.9958 16.0659 15 16.6555C15 17.023 15.0435 17.41 15.129 17.8165L10.722 22.222C10.6508 22.2901 10.5944 22.3722 10.5562 22.463C10.5181 22.5538 10.4989 22.6515 10.5 22.75V25C10.5 25.2025 10.575 25.3795 10.722 25.528H10.7235ZM21.4215 14.8285C21.5278 14.9318 21.6117 15.0557 21.6682 15.1927C21.7247 15.3297 21.7526 15.4768 21.75 15.625C21.7526 15.7732 21.7247 15.9203 21.6682 16.0573C21.6117 16.1943 21.5278 16.3182 21.4215 16.4215C21.3182 16.5278 21.1943 16.6117 21.0573 16.6682C20.9203 16.7247 20.7732 16.7526 20.625 16.75C20.4768 16.7526 20.3297 16.7247 20.1927 16.6682C20.0557 16.6117 19.9318 16.5278 19.8285 16.4215C19.7222 16.3182 19.6383 16.1943 19.5818 16.0573C19.5253 15.9203 19.4974 15.7732 19.5 15.625C19.5 15.313 19.6095 15.0475 19.8285 14.8285C19.9318 14.7222 20.0557 14.6383 20.1927 14.5818C20.3297 14.5253 20.4768 14.4974 20.625 14.5C20.937 14.5 21.2025 14.6095 21.4215 14.8285Z"
            fill="#A8A8A8"
          />
        </svg>{' '}
      </LoginFormIcon>
      <input
        type={togglePassword ? 'text' : 'password'}
        required={required}
        minLength="8"
        name={name ?? 'password'}
        placeholder={placeholder ?? 'Password'}
        onChange={onChange}
      />
      <span style={{ cursor: 'pointer' }} onClick={() => setTogglePassword(!togglePassword)}>
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="16" viewBox="0 0 22 16" fill="none">
          <path
            d="M11 5C10.2044 5 9.44129 5.31607 8.87868 5.87868C8.31607 6.44129 8 7.20435 8 8C8 8.79565 8.31607 9.55871 8.87868 10.1213C9.44129 10.6839 10.2044 11 11 11C11.7956 11 12.5587 10.6839 13.1213 10.1213C13.6839 9.55871 14 8.79565 14 8C14 7.20435 13.6839 6.44129 13.1213 5.87868C12.5587 5.31607 11.7956 5 11 5ZM11 13C9.67392 13 8.40215 12.4732 7.46447 11.5355C6.52678 10.5979 6 9.32608 6 8C6 6.67392 6.52678 5.40215 7.46447 4.46447C8.40215 3.52678 9.67392 3 11 3C12.3261 3 13.5979 3.52678 14.5355 4.46447C15.4732 5.40215 16 6.67392 16 8C16 9.32608 15.4732 10.5979 14.5355 11.5355C13.5979 12.4732 12.3261 13 11 13ZM11 0.5C6 0.5 1.73 3.61 0 8C1.73 12.39 6 15.5 11 15.5C16 15.5 20.27 12.39 22 8C20.27 3.61 16 0.5 11 0.5Z"
            fill="#A8A8A8"
          />
        </svg>
      </span>
    </LoginFormFieldWrapper>
  );
};
const Login = () => {
  const theme = useTheme();
  const mediumScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const { setIsLoggedIn, loading, setLoading } = useContext(MainContext);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const handleUsernameChange = (event) => {
    setError(false);
    setUsername(event.target.value);
  };

  const handlePasswordChange = (event) => {
    setError(false);
    setPassword(event.target.value);
  };

  const loginUser = async (username, password) => {
    let params = {
      email: username,
      password: password,
    };
    try {
      let result = await login(params);
      setLoading(false);
      if (result.token != null) {
        if (result.token) {
          setAccessToken(result);
          setUserType(result.user.user_role_id);
          setIsLoggedIn(true);
          //remember me
          if (remember) {
            setLocalStorage('YWH_REMEMBER_ME_LOGIN', JSON.stringify({ username, password }));
          }
        }

        if (result.user.user_role_id === 2) {
          navigate(routes.agentList());
        } else if (result.agent && result.user.user_role_id === 1) {
          setLocalStorage('agent_id', result.agent.id);
          navigate(routes.agentProfile().replace(':agent_id', result.agent.id));
        } else {
          setError('Something went wrong.');
        }
      } else {
        setError('Invalid username / password.');
      }
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    loginUser(username, password);
  };

  const handleRememberMe = (e) => {
    setRemember(e.target.checked);
  };

  useEffect(() => {
    const rememberLogin = getLocalStorage('YWH_REMEMBER_ME_LOGIN');
    if (rememberLogin && rememberLogin !== '' && rememberLogin !== null) {
      const parsedLogin = JSON.parse(rememberLogin);
      loginUser(parsedLogin.username, parsedLogin.password);
    }
  }, []);

  return (
    <LoginFormContainer>
      <LoginFormWrapper>
        <LoginFormImage src={mediumScreen ? '/login-form__image-md.webp' : '/login-form__image.webp'} />
        <LoginFormForm>
          <LoginFormHeading>
            <h3>Welcome to</h3>
            <h2>
              YOUR<span>WAY</span>HOME
            </h2>
          </LoginFormHeading>
          <LoginFormContents onSubmit={handleSubmit}>
            <LoginFormFields>
              <LoginFormFieldWrapper>
                <LoginFormIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="26" viewBox="0 0 24 26" fill="none">
                    <path
                      d="M10.17 24.5C10.0569 24.1787 9.99944 23.8406 10 23.5V16.5C10 15.732 10.289 15.03 10.764 14.5H3C2.20435 14.5 1.44129 14.8161 0.87868 15.3787C0.316071 15.9413 0 16.7044 0 17.5V18.215C0 21.933 4.21 24.5 10 24.5H10.17ZM16 6.5C16 4.9087 15.3679 3.38258 14.2426 2.25736C13.1174 1.13214 11.5913 0.5 10 0.5C8.4087 0.5 6.88258 1.13214 5.75736 2.25736C4.63214 3.38258 4 4.9087 4 6.5C4 8.0913 4.63214 9.61742 5.75736 10.7426C6.88258 11.8679 8.4087 12.5 10 12.5C11.5913 12.5 13.1174 11.8679 14.2426 10.7426C15.3679 9.61742 16 8.0913 16 6.5ZM13.5 14.5C12.837 14.5 12.2011 14.7634 11.7322 15.2322C11.2634 15.7011 11 16.337 11 17V17.191L17.5 20.441L24 17.191V17C24 16.337 23.7366 15.7011 23.2678 15.2322C22.7989 14.7634 22.163 14.5 21.5 14.5H13.5ZM24 18.309L17.724 21.447C17.6545 21.4818 17.5778 21.5 17.5 21.5C17.4222 21.5 17.3455 21.4818 17.276 21.447L11 18.31V23C11 23.663 11.2634 24.2989 11.7322 24.7678C12.2011 25.2366 12.837 25.5 13.5 25.5H21.5C22.163 25.5 22.7989 25.2366 23.2678 24.7678C23.7366 24.2989 24 23.663 24 23V18.309Z"
                      fill="#A8A8A8"
                    />
                  </svg>
                </LoginFormIcon>
                <input type="text" name="username" required placeholder="Email" onChange={handleUsernameChange} />
              </LoginFormFieldWrapper>
              <InputPassword name="password" onChange={handlePasswordChange} />
            </LoginFormFields>
            <LoginFormFooter>
              {error && <Alert severity="error">{error} </Alert>}
              <LoginFormFooterCol>
                {loading ? <Loader /> : <button type="submit">SIGN IN</button>}
                <Link to={routes.requestPasswordReset()}>Forgot Password?</Link>
              </LoginFormFooterCol>
              <LoginFormFooterColRegister>
                <LoginFormFooterColRegisterText>
                  <Checkbox onChange={handleRememberMe} />
                  <span>Remember me</span>
                </LoginFormFooterColRegisterText>
                <LoginFormFooterColRegisterTextCol>
                  <p>No account yet?</p>
                  <Link to={routes.register()}>REGISTER NOW</Link>
                </LoginFormFooterColRegisterTextCol>
              </LoginFormFooterColRegister>
              <LoginFormFooterTerms>
                By signing up, you agree to The New Home Collection's <br />
                <Link>Terms and Conditions</Link> & <Link>Privacy Policy</Link>.
              </LoginFormFooterTerms>
            </LoginFormFooter>
          </LoginFormContents>
        </LoginFormForm>
      </LoginFormWrapper>
    </LoginFormContainer>
  );
};

export default memo(Login);
